import {
    COLUMN_TYPE_OPTIONS,
    CUSTOM_COLOR_CARET,
    DEFAULT_ORG_ID,
    LENDER_PERSONA_TABS,
    OPTION_COLOR_PRESETS,
    PROTECTED_FIELDS,
    TAB_TYPE_EXCLUSIONS,
    WidthUnit,
} from './PlatformConfigConstants';
import {
    ConfigDescriptor,
    ConfigOperation,
    EffectiveConfigs,
    PersonaAccess,
    SectionDefinition,
    ViewConfig,
    ViewConfigColumn,
    ViewConfigPageData,
} from './PlatformConfigTypes';
import type { PlatformConfigTabKey } from './PlatformConfigConstants';
import { DataTableColumnType } from '../../../common/_custom/DataTable/DataTable.types';

/** Build effective config map: PNO doc wins per role; fallback to DEFAULT (organisationId==="0000000000000000"). */
export function buildEffectiveConfigs(configs: ViewConfig[]): EffectiveConfigs {
    const pno = (role: string) => configs.find((c) => c.role === role && c.organisationId !== DEFAULT_ORG_ID) ?? null;
    const def = (role: string) => configs.find((c) => c.role === role && c.organisationId === DEFAULT_ORG_ID) ?? null;
    return {
        allAccess: pno('allAccess') ?? def('allAccess'),
        partnerAccess: pno('partnerAccess') ?? def('partnerAccess'),
        assesseeAccess: pno('assesseeAccess') ?? def('assesseeAccess'),
    };
}

function getColFromDoc(doc: ViewConfig | null, key: PlatformConfigTabKey, field: string): ViewConfigColumn | undefined {
    const page = doc?.dataConfig?.additionalDetailsConfig?.[key] as ViewConfigPageData | undefined;
    return page?.columns?.find((c) => c.field === field);
}

/** Read persona access for a column from the persona docs. */
export function readPersonaAccess(column: ViewConfigColumn, tab: PlatformConfigTabKey, effective: EffectiveConfigs): PersonaAccess {
    const partnerCol = getColFromDoc(effective.partnerAccess, tab, column.field);
    const borrowerCol = getColFromDoc(effective.assesseeAccess, tab, column.field);
    const lenderPage = effective.allAccess?.dataConfig?.additionalDetailsConfig?.lenderHeaders as ViewConfigPageData | undefined;
    const lenderCol = lenderPage?.columns?.find((c) => c.field === column.field);

    // A column ABSENT from a persona's doc is neither visible nor editable for that
    // persona — the persona role docs (visibility config) are the source of truth.
    const visible = (c?: ViewConfigColumn) => !!c && c.show !== false;
    const editable = (c?: ViewConfigColumn) => visible(c) && !c!.disable;
    return {
        partnerVisible: visible(partnerCol),
        partnerEditable: editable(partnerCol),
        borrowerVisible: visible(borrowerCol),
        borrowerEditable: editable(borrowerCol),
        lenderVisible: visible(lenderCol),
        lenderEditable: editable(lenderCol),
    };
}

/** Produce the camelCase slug from a display label. */
export function slugFromLabel(label: string): string {
    return label
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .split(' ')
        .filter(Boolean)
        .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
        .join('');
}

/**
 * Namespaced field key for a newly-added column.
 * OWNER-typed columns MUST live under the `owners.` namespace — the owner cell
 * renderer and save path read/write `row.owners[<key>]` (they strip the prefix
 * and use only the segment after the dot). A `customFields.`-prefixed OWNER
 * column is broken end-to-end: the edit writes to a non-existent owner slot and
 * the cell reads `owners.<key>` which is never populated. Everything else is a
 * `customFields.` attribute. Returns '' when the label is empty (mirrors the
 * prior `label ? ... : ''` behaviour so the empty-slug warning still fires).
 */
export function buildGeneratedFieldKey(label: string, type: string): string {
    if (!label) return '';
    const namespace = type === DataTableColumnType.OWNER ? 'owners' : 'customFields';
    return `${namespace}.${slugFromLabel(label)}`;
}

export function isProtectedField(col: ViewConfigColumn, descriptor?: ConfigDescriptor | null): boolean {
    if (descriptor) {
        // Check all sections for protectedItems
        return Object.values(descriptor.sections ?? {}).some((sec) => sec.protectedItems?.includes(col.field));
    }
    // Fallback to local constant if descriptor not yet loaded
    return PROTECTED_FIELDS.has(col.field);
}

/**
 * Derive which tabs (section keys) are writable from the descriptor.
 * Returns only the section keys the descriptor marks writable:true.
 * Falls back to the full PLATFORM_CONFIG_TABS values if the descriptor is absent.
 */
export function getWritableSections(descriptor: ConfigDescriptor | null, fallbackKeys: string[]): string[] {
    if (!descriptor?.sections) return fallbackKeys;
    return fallbackKeys.filter((key) => {
        const sec: SectionDefinition | undefined = descriptor.sections[key];
        return sec ? sec.writable : false;
    });
}

/**
 * Resolve whether a field attribute is editable for a given item, using the descriptor's itemSchema.
 *
 *   editableOn === 'ALL'    → always editable
 *   editableOn === 'CUSTOM' → editable only when item[customMarker] === true
 *   editableOn === 'NONE'   → never editable
 */
export function isAttrEditable(attrName: string, item: ViewConfigColumn, descriptor: ConfigDescriptor | null): boolean {
    if (!descriptor) return true; // no descriptor yet — allow
    const schema = descriptor.itemSchema?.[attrName];
    if (!schema) return true; // attr not governed — allow
    // Admin can change any governed attribute except identity/marker (editableOn=NONE).
    return schema.editableOn !== 'NONE';
}

/**
 * Remove the types a tab's TAB_TYPE_EXCLUSIONS entry marks as not applicable to its entity
 * (G-5) — applied regardless of whether `types` came from the descriptor or the
 * local fallback list, so an excluded type is never offered no matter the source.
 */
export function filterAllowedColumnTypesForTab(types: string[], tab: PlatformConfigTabKey): string[] {
    const excluded = TAB_TYPE_EXCLUSIONS[tab];
    if (!excluded || excluded.length === 0) return types;
    const excludedSet = new Set<string>(excluded);
    return types.filter((t) => !excludedSet.has(t));
}

// Fallback map for type display names (mirrors COLUMN_TYPE_OPTIONS)
const FALLBACK_TYPE_LABELS: Record<string, string> = Object.fromEntries(COLUMN_TYPE_OPTIONS.map((o) => [o.id, o.label]));

/**
 * Resolve a human-readable display name for a column type id.
 * Descriptor's displayNames win; falls back to the local constant map; then the raw id.
 */
export function resolveTypeDisplayName(typeId: string, descriptor: ConfigDescriptor | null): string {
    const fromDescriptor = descriptor?.itemSchema?.type?.displayNames?.[typeId];
    return fromDescriptor ?? FALLBACK_TYPE_LABELS[typeId] ?? typeId;
}

export interface PersonaPill {
    label: string;
    /** persona key, used for per-persona CSS colouring */
    persona: 'admin' | 'partner' | 'borrower' | 'lender';
}

/**
 * Derive the list of non-Admin "Visible To" pills for a column row.
 * Admin is always visible — the caller prepends an Admin pill separately.
 * Returns an empty array for admin-only tabs.
 * Lender is only included on tabs where lender persona is meaningful.
 */
export function deriveVisibleToPills(access: PersonaAccess | null, tab: PlatformConfigTabKey, isAdminOnly: boolean): PersonaPill[] {
    if (isAdminOnly || !access) return [];
    const pills: PersonaPill[] = [];
    if (access.partnerVisible) pills.push({ label: 'Partner', persona: 'partner' });
    if (access.borrowerVisible) pills.push({ label: 'Borrower', persona: 'borrower' });
    if (LENDER_PERSONA_TABS.has(tab) && access.lenderVisible) pills.push({ label: 'Lender', persona: 'lender' });
    return pills;
}

/**
 * Derive the list of non-Admin "Editable By" pills for a column row.
 * Admin is always editable — the caller prepends an Admin pill separately.
 * Lender is only included on tabs where lender persona is meaningful.
 */
export function deriveEditableByPills(access: PersonaAccess | null, tab: PlatformConfigTabKey): PersonaPill[] {
    if (!access) return [];
    const pills: PersonaPill[] = [];
    if (access.partnerEditable) pills.push({ label: 'Partner', persona: 'partner' });
    if (access.borrowerEditable) pills.push({ label: 'Borrower', persona: 'borrower' });
    if (LENDER_PERSONA_TABS.has(tab) && access.lenderEditable) pills.push({ label: 'Lender', persona: 'lender' });
    return pills;
}

/**
 * Determine whether the Manage Fields route should be accessible.
 *
 * rawConfigValue is the raw value from the visibility config map for the
 * MANAGE_FIELDS key (undefined when the key is absent / PNO not yet seeded).
 *   >= 0  → config present and grants access
 *   -1    → config present and explicitly hides it
 *   undefined → key absent → fall back to SUPER_ADMIN role check
 */
export function resolveManageFieldsGate(rawConfigValue: number | boolean | string | undefined, isSuperAdmin: boolean): boolean {
    if (rawConfigValue === undefined) return isSuperAdmin;
    // boolean true/false (legacy shape) or numeric level
    if (typeof rawConfigValue === 'boolean') return rawConfigValue;
    // MANAGE_FIELDS never carries a string value; VisibilityConfigMap's value type widened to
    // include string for other keys (e.g. Credits' pnoIdEcho) — never a valid level here.
    if (typeof rawConfigValue === 'string') return false;
    return rawConfigValue >= 0;
}

/**
 * Silently reconcile a persisted doc (returned by postOps) back into the configs list.
 * Matches by role; replaces the entry if found, appends otherwise (COW fork: new _id).
 * Returns new arrays for both configs and the derived effective map.
 */
export function mergeSavedDoc(configs: ViewConfig[], savedDoc: ViewConfig): ViewConfig[] {
    const idx = configs.findIndex((c) => c.role === savedDoc.role);
    if (idx < 0) return [...configs, savedDoc];
    const next = [...configs];
    next[idx] = savedDoc;
    return next;
}

export type ReorderAction = 'up' | 'down' | 'top' | 'bottom' | number;

/**
 * Apply a reorder action (named move or explicit target index, e.g. from drag-drop)
 * to a column list, returning a new array. Pure function — no mutation.
 */
export function applyReorder(cols: ViewConfigColumn[], field: string, action: ReorderAction): ViewConfigColumn[] {
    const idx = cols.findIndex((c) => c.field === field);
    if (idx < 0) return cols;
    let toIndex = idx;
    if (typeof action === 'number') toIndex = Math.max(0, Math.min(cols.length - 1, action));
    else if (action === 'up') toIndex = Math.max(0, idx - 1);
    else if (action === 'down') toIndex = Math.min(cols.length - 1, idx + 1);
    else if (action === 'top') toIndex = 0;
    else toIndex = cols.length - 1;
    if (toIndex === idx) return cols;
    const next = [...cols];
    const [item] = next.splice(idx, 1);
    next.splice(toIndex, 0, item);
    return next;
}

// ── Width helpers ─────────────────────────────────────────────────────────────

/**
 * Parse a stored width string into a numeric value + unit for the drawer editor.
 * "150px" → {value:"150", unit:"px"}, "10%" → {value:"10", unit:"%"},
 * "150" (bare int, legacy) → {value:"150", unit:"px"}, undefined → {value:"", unit:"px"}
 */
export function parseWidth(width?: string): { value: string; unit: WidthUnit } {
    if (!width) return { value: '', unit: 'px' };
    const pct = width.match(/^(\d+(?:\.\d+)?)%$/);
    if (pct) return { value: pct[1], unit: '%' };
    const px = width.match(/^(\d+(?:\.\d+)?)(?:px)?$/);
    if (px) return { value: px[1], unit: 'px' };
    // Unrecognised unit — surface the numeric part only, default to px
    const num = width.match(/^(\d+(?:\.\d+)?)/);
    return { value: num ? num[1] : '', unit: 'px' };
}

/**
 * Combine a numeric value string + unit into the stored width string.
 * Empty value → returns undefined (width omitted from saved column).
 */
export function formatWidth(value: string, unit: WidthUnit): string | undefined {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return `${trimmed}${unit}`;
}

// ── Dropdown option helpers ───────────────────────────────────────────────────

export interface DropdownOptionMap {
    [value: string]: { label: string; value: string; backgroundColor: string; labelColor: string; customCaret?: string };
}

/**
 * Derive the NAME_LABEL value key from a human-readable label.
 * "New" → "NEW", "Attempt Being Made" → "ATTEMPT_BEING_MADE"
 * Leading/trailing underscores are stripped; empty input returns "".
 */
export function deriveOptionValue(label: string): string {
    return label
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

// Preset lookup map keyed by id — built once
const PRESET_BY_ID = Object.fromEntries(OPTION_COLOR_PRESETS.map((p) => [p.id, p]));

// Preset lookup map keyed by "backgroundColor|labelColor" — used for reverse-mapping
const PRESET_BY_COLORS = Object.fromEntries(OPTION_COLOR_PRESETS.map((p) => [`${p.backgroundColor}|${p.labelColor}`, p]));

export interface OptionEditorItem {
    label: string;
    colorPresetId?: string;
    backgroundColor?: string;
    labelColor?: string;
}

/**
 * Convert the editor options array to the dropdownOptions map shape the
 * renderers consume. Resolves bg/label/caret from the preset when colorPresetId
 * is set; uses stored hex + grey caret for 'custom'. Options with a blank label
 * are skipped. On duplicate derived keys, first-wins.
 */
export function optionsArrayToMap(opts: OptionEditorItem[]): DropdownOptionMap {
    const map: DropdownOptionMap = {};
    for (const o of opts) {
        if (!o.label.trim()) continue;
        const value = deriveOptionValue(o.label);
        if (!value) continue;
        if (map[value]) continue; // first-wins on duplicate derived key

        const preset = o.colorPresetId && o.colorPresetId !== 'custom' ? PRESET_BY_ID[o.colorPresetId] : null;
        const backgroundColor = preset ? preset.backgroundColor : o.backgroundColor ?? '';
        const labelColor = preset ? preset.labelColor : o.labelColor ?? '';
        const customCaret = preset ? preset.customCaret : CUSTOM_COLOR_CARET;

        map[value] = { label: o.label, value, backgroundColor, labelColor, customCaret };
    }
    return map;
}

/**
 * Convert a dropdownOptions map back to an editor-item array.
 * Reverse-maps stored bg+label colors to a preset id when they match exactly;
 * falls back to colorPresetId='custom' with the stored hex values.
 */
export function optionsMapToArray(dropdownOptions: DropdownOptionMap | undefined): OptionEditorItem[] {
    if (!dropdownOptions) return [];
    return Object.values(dropdownOptions).map((o) => {
        const key = `${o.backgroundColor}|${o.labelColor}`;
        const preset = PRESET_BY_COLORS[key];
        if (preset) return { label: o.label, colorPresetId: preset.id };
        return { label: o.label, colorPresetId: 'custom', backgroundColor: o.backgroundColor, labelColor: o.labelColor };
    });
}

/**
 * Pure predicate for whether the ColumnDrawer's Save button should be disabled.
 * Mirrors the inline expression previously in ColumnDrawer.tsx verbatim.
 */
export function isColumnSaveDisabled(p: {
    saving: boolean;
    label: string;
    emptySlugWarning: boolean;
    isSystem: boolean;
    type: string;
    widthValue: string | number;
    options: { label: string }[];
    isDropdownType: boolean;
}): boolean {
    return (
        p.saving ||
        !p.label.trim() ||
        p.emptySlugWarning ||
        (!p.isSystem && (!p.type || !String(p.widthValue ?? '').trim())) ||
        (p.isDropdownType && p.options.filter((o) => o.label.trim()).length === 0)
    );
}

/**
 * Compute the op list for a drawer save.
 *
 * ADD mode  → one ADD_ITEM op; payload is the column WITHOUT the customMarker field
 *             (engine stamps it); toIndex included if provided.
 * EDIT mode → one UPDATE_ITEM op whose payload contains ONLY the changed attributes
 *             (diff of edited vs original).
 */
export function buildOperations(
    original: ViewConfigColumn | null,
    edited: ViewConfigColumn,
    section: string,
    opts?: { toIndex?: number; customMarker?: string },
): ConfigOperation[] {
    if (!original) {
        // ADD
        const payload: Record<string, unknown> = { ...edited };
        // Strip the customMarker — engine stamps it
        const marker = opts?.customMarker ?? 'isCustom';
        delete payload[marker];
        const op: ConfigOperation = { op: 'ADD_ITEM', section, payload };
        if (opts?.toIndex !== undefined) op.toIndex = opts.toIndex;
        return [op];
    }

    // EDIT — send only changed attrs
    const diff: Record<string, unknown> = {};
    (Object.keys(edited) as (keyof ViewConfigColumn)[]).forEach((key) => {
        const a = JSON.stringify(original[key]);
        const b = JSON.stringify(edited[key]);
        if (a !== b) diff[key] = edited[key];
    });

    if (Object.keys(diff).length === 0) return []; // nothing changed

    return [{ op: 'UPDATE_ITEM', section, itemId: original.field, payload: diff }];
}

/**
 * Produce ops for a persona doc given its section key and show/disable override.
 * Item present → UPDATE_ITEM diff of show/disable. Item absent → ADD_ITEM copy of
 * the column with persona overrides, stripped of the custom marker and the
 * field-history flags (persona copies — partner/assessee docs and the allAccess lenderHeaders
 * section — never carry them; recording reads only the allAccess main sections).
 */
export function buildPersonaOps(
    doc: ViewConfig | null,
    section: string,
    column: ViewConfigColumn,
    show: boolean,
    disable: boolean,
    customMarker = 'isCustom',
): ConfigOperation[] {
    if (!doc) return [];
    const existingItem = getColFromDoc(doc, section as PlatformConfigTabKey, column.field);
    if (existingItem) {
        const diff: Record<string, unknown> = {};
        if (existingItem.show !== show) diff.show = show;
        if (existingItem.disable !== disable) diff.disable = disable;
        if (Object.keys(diff).length === 0) return [];
        return [{ op: 'UPDATE_ITEM', section, itemId: column.field, payload: diff }];
    }
    // Item absent → ADD with column def from allAccess + persona overrides
    const payload: Record<string, unknown> = { ...column, show, disable };
    delete payload[customMarker];
    // Persona copies (incl. allAccess lenderHeaders) never carry field-history flags.
    delete payload.is_audit_enabled;
    delete payload.isHistoryEnabled;
    return [{ op: 'ADD_ITEM', section, payload }];
}
